import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";

import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  AppBar,
  Toolbar,
  Container,
  Backdrop,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";


/**
 * Renders the public apply page.
 *
 * This function renders the public apply page component which allows users to apply for a job.
 * It fetches the job description using the provided job ID and displays it to the user.
 * The user can then enter their resume URL and submit their application.
 *
 * @return {JSX.Element} The public apply page component.
 */
function PublicApply() {
  const { jdId } = useParams();
  const [jd, setJd] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    resume: null,
  });

  /**
   * Fetches the job description using the provided job ID.
   *
   * @return {Promise<void>} A Promise that resolves when the job description is fetched.
   * @throws {Error} If the job description can't be fetched.
   */
  useEffect(() => {
    async function fetchJobDescription() {
      try {
        const res = await api.get(`/public/job/${jdId}/`);
        setJd(res.data);
      } catch {
        setDialog({
          open: true,
          title: "Invalid Job Link",
          message: "This job link is no longer valid or has expired.",
        });
      }
    }

    fetchJobDescription();
  }, [jdId]);

  /**
   * Submits the candidate application form.
   *
   * This function validates the form inputs and submits the application to the server.
   * If any input is missing or invalid, it sets the appropriate error message.
   * On successful submission, it sets the `submitted` state to `true`.
   *
   * @return {Promise<void>} A Promise that resolves when the form is submitted.
   * @throws {Error} If any input is missing or invalid.
   */
  const submit = async () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Full name is required";
    if (!form.email.trim()) newErrors.email = "Valid email is required";
    if (!form.mobile.trim()) newErrors.mobile = "Mobile number is required";
    if (!form.resume) newErrors.resume = "Resume is required";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setErrors(
        <span style={{ color: "#ff4d4d" }}>
          Please enter a valid email address
        </span>
      );
      return;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);

    const fd = new FormData();
    fd.append("jd_id", jdId);
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("mobile", form.mobile);
    fd.append("resume", form.resume);

    try {
      await api.post("/public/apply/", fd);
      setSubmitted(true);
    } catch {
      setDialog({
        open: true,
        title: "Application Failed",
        message: "Something went wrong. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Formats a value with a fallback text.
   *
   * @param {string} value - The value to format.
   * @param {string} fallback - The fallback text to use if the value is not specified.
   * @returns {Object} An object with the formatted text and a boolean indicating if it should be dimmed.
   */
  const formatWithFallback = (value, fallback) => {
    if (!value) {
      return { text: fallback, dim: true };
    }

    const v = value.toLowerCase();
    if (v.includes("not specified") || v === "n/a") {
      return { text: fallback, dim: true };
    }

    return { text: value, dim: false };
  };

  const location = formatWithFallback(jd?.job_location, "Location");
  const exp = formatWithFallback(jd?.years_of_experience, "Exp");
  const type = formatWithFallback(jd?.job_type, "Job type");
  const salary = formatWithFallback(jd?.salary_range, "Salary range");

  if (submitted) {
    return (
      <Box textAlign="center" mt={10}>
        <Card sx={{ maxWidth: 500, mx: "auto", p: 5 }}>
          <Typography variant="h4" fontWeight={700}>
            Application Submitted!
          </Typography>
          <Typography mt={2}>
            Our team will get back to you soon.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.9)",
          borderBottom: "1px solid #eee",
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              background: "linear-gradient(90deg,#6366f1,#ec4899)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            ITConnectUS
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Box
          sx={{
            display: "flex",
            gap: 5,
            alignItems: "flex-start",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ flex: 7 }}>
            <Card sx={{ p: 6 }}>
              <Typography variant="h4" fontWeight={800} gutterBottom>
                {jd?.job_title || "Loading..."}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" mb={3}>
                <Chip label={`📍 ${location.text}`} sx={{ color: location.dim ? "#9e9e9e" : "#000" }} />
                <Chip label={`🧠 ${exp.text}`} sx={{ color: exp.dim ? "#9e9e9e" : "#000" }} />
                <Chip label={`💼 ${type.text}`} sx={{ color: type.dim ? "#9e9e9e" : "#000" }} />
                <Chip label={`💰 ${salary.text}`} sx={{ color: salary.dim ? "#9e9e9e" : "#000" }} />
              </Stack>

              {jd?.job_summary && (
                <Box mt={4}>
                  <Typography variant="h6" fontWeight={700}>
                    About the Role
                  </Typography>
                  <ul>
                    {jd.job_summary.split("\n").map((r, i) => (
                      <li key={i}>
                        <Typography>{r}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}

              {jd?.responsibilities && (
                <>
                  <Typography variant="h6" fontWeight={700} mt={3}>
                    Responsibilities
                  </Typography>
                  <Box component="ul" sx={{ pl: 3 }}>
                    {jd.responsibilities.split("\n").map((r, i) => (
                      <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                      </li>
                    ))}
                  </Box>
                </>
              )}

              {jd?.required_qualifications && (
                <>
                  <Typography variant="h6" fontWeight={700} mt={3}>
                    Required Qualifications
                  </Typography>
                  <Box component="ul" sx={{ pl: 3 }}>
                    {jd.required_qualifications.split("\n").map((r, i) => (
                      <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                      </li>
                    ))}
                  </Box>
                </>
              )}
              <br />
              <Typography variant="h5" gutterBottom>
                If you want to see more job openings, please click{" "}
                <Button
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    onClick={() => navigate(`/`)}
                >
                    Here
                </Button>
            </Typography>
            </Card>
          </Box>

          <Box
            sx={{
              flex: 3,
              position: { md: "sticky" },
              top: { md: 100 },
              width: "100%",
            }}
          >
            <Card sx={{ p: 5, position: "relative" }}>
              <Typography variant="h5" fontWeight={700} mb={3}>
                Apply Now
              </Typography>

              <TextField
                fullWidth
                label="Full Name"
                placeholder="Enter Full Name *"
                margin="normal"
                value={form.name}
                error={!!errors.name}
                helperText={errors.name}
                onChange={(e) =>
                  setForm({ ...form, name: e.target.value })
                }
              />

              <TextField
                fullWidth
                label="Email"
                placeholder="Enter Email *"
                margin="normal"
                value={form.email}
                error={!!errors.email}
                helperText={errors.email}
                onChange={(e) =>
                  setForm({ ...form, email: e.target.value })
                }
              />

              <TextField
                fullWidth
                label="Mobile"
                placeholder="Enter Mobile Number *"
                margin="normal"
                value={form.mobile}
                inputProps={{ maxLength: 14 }}
                error={!!errors.mobile}
                helperText={errors.mobile}
                onChange={(e) =>
                  setForm({ ...form, mobile: e.target.value })
                }
              />

              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload Resume *
                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setForm({ ...form, resume: e.target.files[0] })
                  }
                />
              </Button>

              {errors.resume && (
                <Typography color="error" fontSize={13} mt={1}>
                  {errors.resume}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3 }}
                onClick={submit}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>

              {loading && (
                <Backdrop
                  open
                  sx={{
                    position: "absolute",
                    borderRadius: 2,
                    zIndex: 10,
                  }}
                >
                  <CircularProgress color="inherit" />
                </Backdrop>
              )}
            </Card>
          </Box>
        </Box>
      </Container>
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ ...dialog, open: false })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography fontSize="14px">
            {dialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setDialog({ ...dialog, open: false })}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Box textAlign="center" py={5} sx={{ opacity: 0.6 }}>
        <Typography variant="body2">
          © {new Date().getFullYear()} {api.app_name}
        </Typography>
      </Box>
    </>
  );
}

export default PublicApply;
